import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, AlertTriangle } from "lucide-react";

const AUTO_LABEL = {
  trial_invitation: "Trial invitation",
  reminder_3d: "3-day reminder",
  reminder_1d: "1-day reminder",
  expiry_confirmation: "Expiry confirmation",
  abandoned_checkout: "Abandoned checkout",
};
const STATUS_COLOR = {
  queued: "text-donkey-amber", sent: "text-donkey-green", skipped: "text-donkey-grey",
  failed: "text-donkey-red", suppressed: "text-donkey-grey", cancelled: "text-donkey-grey",
};

export default function AdminEmail() {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [fStatus, setFStatus] = useState("");
  const [fAuto, setFAuto] = useState("");
  // invitation form
  const [iEmail, setIEmail] = useState("");
  const [iTier, setITier] = useState("prove");
  const [iDur, setIDur] = useState(14);
  const [iReason, setIReason] = useState("");
  const [iSource, setISource] = useState("");

  const loadStatus = useCallback(async () => { const { data } = await api.get("/admin/email/status"); setStatus(data); }, []);
  const loadMessages = useCallback(async () => {
    const { data } = await api.get("/admin/email/messages", { params: { status: fStatus, automation: fAuto } });
    setMessages(data.messages);
  }, [fStatus, fAuto]);
  const loadInvites = useCallback(async () => { const { data } = await api.get("/admin/comp/invitations"); setInvitations(data.invitations); }, []);

  useEffect(() => { loadStatus(); loadInvites(); }, [loadStatus, loadInvites]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  const toggleGlobal = async (v) => {
    try { const { data } = await api.post("/admin/email/settings", { global_enabled: v }); setStatus(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const toggleAuto = async (k, v) => {
    try { const { data } = await api.post("/admin/email/settings", { automations: { [k]: v } }); setStatus(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const createInvite = async () => {
    if (!iEmail || !iReason.trim() || !iSource.trim()) return toast.error("Email, reason and consent source are required.");
    try {
      await api.post("/admin/comp/invitations", { email: iEmail, tier: iTier, duration_days: Number(iDur), reason: iReason.trim(), source: iSource.trim() });
      toast.success("Invitation recorded."); setIEmail(""); setIReason(""); setISource("");
      await loadInvites(); await loadMessages();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const retry = async (id) => { try { await api.post(`/admin/email/messages/${id}/retry`); toast.success("Retried."); await loadMessages(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } };
  const cancel = async (id) => { try { await api.post(`/admin/email/messages/${id}/cancel`); toast.success("Cancelled."); await loadMessages(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } };

  return (
    <div className="space-y-6" data-testid="admin-email">
      {/* Provider status */}
      <div className="card-donkey rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-donkey-orange" /><h3 className="font-heading font-semibold text-donkey-warm text-sm">Email automation</h3></div>
        {status && (
          <div className="flex flex-wrap items-center gap-4">
            <span className={`text-sm px-2 py-1 rounded ${status.connected ? "text-donkey-green bg-donkey-green/10" : "text-donkey-amber bg-donkey-amber/10"}`} data-testid="email-provider-status">
              Resend: {status.connected ? "connected" : "not connected"}
            </span>
            <label className="flex items-center gap-2 text-sm text-donkey-warm">
              Global sending
              <Switch checked={status.global_enabled} onCheckedChange={toggleGlobal} data-testid="email-global-toggle" />
            </label>
          </div>
        )}
        {status && !status.connected && (
          <p className="text-xs text-donkey-grey mt-3 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-donkey-amber shrink-0 mt-0.5" />Nothing is sent or queued for delivery until Resend is configured (API key + verified sending domain) and each automation is enabled below.</p>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
          {status && Object.keys(AUTO_LABEL).map((k) => (
            <label key={k} className="flex items-center justify-between gap-2 text-sm text-donkey-warm border border-white/10 rounded-lg px-3 py-2">
              {AUTO_LABEL[k]}
              <Switch checked={status.automations[k]} onCheckedChange={(v) => toggleAuto(k, v)} data-testid={`email-auto-${k}`} />
            </label>
          ))}
        </div>
      </div>

      {/* Invitation form */}
      <div className="card-donkey rounded-2xl p-5">
        <h3 className="font-heading font-semibold text-donkey-warm text-sm mb-3">Invite a user to a complimentary trial</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <Input value={iEmail} onChange={(e) => setIEmail(e.target.value)} placeholder="email" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="invite-email" />
          <select value={iTier} onChange={(e) => setITier(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="invite-tier">
            {["prove", "build", "launch", "grow", "scale"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={iDur} onChange={(e) => setIDur(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="invite-duration">
            {[7, 14, 30].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
          <Input value={iReason} onChange={(e) => setIReason(e.target.value)} placeholder="reason (required)" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="invite-reason" />
          <Input value={iSource} onChange={(e) => setISource(e.target.value)} placeholder="consent source (required)" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="invite-source" />
        </div>
        <Button onClick={createInvite} className="mt-3 bg-donkey-orange text-donkey-black font-semibold" data-testid="invite-submit">Create invitation</Button>
        <p className="text-xs text-donkey-grey mt-2">Existing users get the grant immediately; new emails receive a single-use link that activates the trial only when they register with that exact address. The invitation email is held until sending is enabled.</p>
        {invitations.length > 0 && (
          <div className="mt-4 space-y-1 max-h-40 overflow-auto">
            {invitations.slice(0, 20).map((i) => (
              <div key={i.id} className="text-xs text-donkey-grey">{i.email} · {i.tier} · {i.status} · {i.kind}</div>
            ))}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="card-donkey rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h3 className="font-heading font-semibold text-donkey-warm text-sm flex-1">Outbox</h3>
          <select value={fAuto} onChange={(e) => setFAuto(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-xs px-2 h-9" data-testid="email-filter-auto">
            <option value="">all types</option>
            {Object.keys(AUTO_LABEL).map((k) => <option key={k} value={k}>{AUTO_LABEL[k]}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-xs px-2 h-9" data-testid="email-filter-status">
            <option value="">all statuses</option>
            {["queued", "sent", "skipped", "failed", "suppressed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          {messages.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center gap-3 text-xs border-b border-white/5 py-2" data-testid={`email-msg-${m.id}`}>
              <span className="text-donkey-warm flex-1 min-w-[140px]">{m.email}</span>
              <span className="text-donkey-grey">{AUTO_LABEL[m.automation] || m.automation}</span>
              <span className={STATUS_COLOR[m.status] || "text-donkey-grey"}>{m.status}{m.reason ? ` (${m.reason})` : ""}</span>
              {m.status === "failed" && <button onClick={() => retry(m.id)} className="text-donkey-orange hover:underline" data-testid={`email-retry-${m.id}`}>retry</button>}
              {(m.status === "queued" || m.status === "skipped") && <button onClick={() => cancel(m.id)} className="text-donkey-grey hover:text-donkey-warm hover:underline" data-testid={`email-cancel-${m.id}`}>cancel</button>}
            </div>
          ))}
          {messages.length === 0 && <p className="text-sm text-donkey-grey">No messages.</p>}
        </div>
      </div>
    </div>
  );
}
